import { useEffect, useState, useRef } from 'react'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import SmartMateriaUITable from '../../../generic/SmartMateriaUITable'
import { InsertDriveFile, AddCircleRounded } from '@mui/icons-material'
import { apiGet, apiPostFormData } from '../../../generic/Api_Functions'
import { Button, Typography } from '@mui/material'
import { useAuthorization } from '../../../generic/hooks/useAuthorization'
import AlertUI from '../../../generic/AlertUI'

/**
 * A program is only usable by the caller if it carries the `id` that
 * `JobTask.loadOperationList` turns into `idList` for `getOperationByProgramIds`.
 *
 * ⚠️ This guard exists because of a real failure in a plant: Sun answered
 * `rejected value [NaN] ... For input string: "NaN"` when adding a program to the workflow of a
 * fabrication order. The `NaN` was minted here. In single mode the accept button handed over
 * whatever `selectedPrograms` happened to hold, and that is NOT always a program: the table emits
 * `multipleSelectionChange({rows: [], allSelected: false})` **on mount, whether or not it is in
 * multiple-selection mode** (`SmartMateriaUITable.jsx:1092-1094`), so the state was overwritten with
 * an empty array before the user touched anything. Accepting without picking a row then sent
 * `parseInt(undefined)` straight to the server.
 */
const isUsableProgram = (candidate) =>
  candidate !== null && candidate !== undefined && !Array.isArray(candidate) &&
  candidate.id !== null && candidate.id !== undefined && !Number.isNaN(parseInt(candidate.id))

const AddProgramDialog = ({ open, setOpen, multipleMode, technology, onAddPrograms, onUpdateProgram }) => {
  const { t_ } = useSmartTranslation()
  const [programs, setPrograms] = useState([])
  const [selectedPrograms, setSelectedPrograms] = useState([])
  const fileInputRef = useRef()
  const isUserAuthorized = useAuthorization()
  const [AlertElement, showAlert] = AlertUI()

  useEffect(() => {
    const loadPrograms = async () => {
      const programList = await apiGet('programsByTechnology/' + technology.id)
      setPrograms(programList)
    }
    if (!technology) {
      setPrograms([])
      return
    }
    loadPrograms()
  }, [technology, technology?.id])

  useEffect(() => {
    if (open) {
      setSelectedPrograms(multipleMode ? [] : null)
    }
  }, [open, multipleMode])

  const handleUploadProgram = async (file) => {
    if (!technology) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('technology', technology.id)

    try {
      await apiPostFormData('programs', formData).then(data => {
        showAlert({ severity: 'success', message: t_('Created correctly.') })
      }).catch(error => {
        showAlert({ severity: 'error', title: t_(error.message) })
      })
      const programList = await apiGet('programsByTechnology/' + technology.id)
      setPrograms(programList)
    } catch (error) {
      showAlert({ severity: 'error', title: t_('Error uploading program') })
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      handleUploadProgram(file)
    }
  }

  const defaultToolbar = {
    renderSticky: {
      fetchFunction: () => {
        return (
          <>
            <Button
              disabled={!isUserAuthorized('ADD_PROGRAMS')}
              sx={{ border: 0 }}
              color='primary'
              variant='outlined'
              onClick={() => {
                fileInputRef.current.click()
              }}
            >
              <AddCircleRounded sx={{ fontSize: '1.2em' }} />
              <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('Add program')}</Typography>
            </Button>
            <input
              onChange={handleFileChange}
              ref={fileInputRef}
              type='file'
              hidden
            />
          </>
        )
      }
    }
  }

  return (
    <SmartDialog
      setOpen={open}
      cancelCallback={() => setOpen(false)}
      acceptCallback={() => {
        if (multipleMode) {
          const selection = Array.isArray(selectedPrograms) ? selectedPrograms : []
          const items = selection.map((programData) => programs.find((item) => item.id === parseInt(programData.id)))
          // Nothing picked, or a picked row that no longer matches a loaded program: say so instead
          // of going on. Dropping the odd one out would add the task without its operations and
          // without telling anyone.
          if (items.length === 0 || !items.every(isUsableProgram)) {
            showAlert({ severity: 'error', message: t_('You must select at least one program.') })
            return
          }
          onAddPrograms(items)
        } else {
          if (!isUsableProgram(selectedPrograms)) {
            showAlert({ severity: 'error', message: t_('You must select at least one program.') })
            return
          }
          onUpdateProgram(selectedPrograms)
        }
      }}
      title={createDialogTitle({ title: t_('Add selected programs'), icon: InsertDriveFile })}
      renderComponent={
        <>
          {AlertElement}
          <SmartMateriaUITable
            title={t_('Program')}
            dataFetch={programs}
            columns={[{ name: t_('Name'), field: 'name' }]}
            deleteMultipleSelectionDisabled
            multipleSelectionActions={[]}
            multipleSelection={multipleMode}
            // Each mode listens to ITS OWN event, and only to it. Wiring both let the table's
            // mount-time reset (which fires in either mode) overwrite the single-mode choice with an
            // empty array, and let a row click in multiple mode replace the selected array with one
            // row -- after which accepting did nothing at all, silently.
            multipleSelectionChange={multipleMode ? (multipleSelection) => setSelectedPrograms(multipleSelection.rows) : undefined}
            onRowClick={multipleMode ? undefined : (row) => setSelectedPrograms(row)}
            toolbar={defaultToolbar}
          />
        </>
      }
    />
  )
}

export default AddProgramDialog
